import {
  BookOutline,
  BulbOutline,
  CashOutline,
  CheckmarkCircle,
  ChevronDown,
  PieChartOutline,
  SchoolOutline,
  ShieldCheckmarkOutline,
  TimeOutline,
  TrendingUpOutline,
  WalletOutline,
} from "@raresail/react-ionicons";
import { useState } from "react";
import styled from "styled-components";
import {
  primary5,
  primary10,
  PRIMARY_COLOR,
  PRIMARY_LIGHT,
} from "../../../constants/colors";
import { alpha } from "@mui/material";
import { useTitle } from "../../../hooks/common/useTitle";
import { AnimateChildren, Heading } from "../../elements/Elements";
import { useLocalStorage } from "../../../hooks/common/useLocalStorage";

// ─── Article Data ───────────────────────────────────────────────
type Article = {
  id: string;
  title: string;
  summary: string;
  readTime: number;
  content: string[];
  keyTakeaways: string[];
};

type Category = {
  id: string;
  label: string;
  icon: any;
  color: string;
  description: string;
  articles: Article[];
};

const CATEGORIES: Category[] = [
  {
    id: "basics",
    label: "Money Basics",
    icon: WalletOutline,
    color: "#1565c0",
    description: "Build a strong financial foundation",
    articles: [
      {
        id: "basics-1",
        title: "Saving vs. Investing: What's the Difference?",
        summary: "Understand when to save and when to invest your money for maximum benefit.",
        readTime: 4,
        content: [
          "Saving means putting money aside in a safe, accessible place — like a bank account or mobile money wallet. The goal is preserving your money so it's there when you need it. In Uganda, savings account interest rates at commercial banks are typically between 2-5% per year, which often doesn't keep up with inflation.",
          "Investing means putting your money into something that has the potential to grow — like unit trust funds, government bonds, or lending platforms like Everpesa. Unlike savings, investments carry some risk, but they also offer much higher returns. Money Market Funds in Uganda currently offer 9-12% per year.",
          "A good rule: keep 3-6 months of living expenses in savings for emergencies. Everything above that should be invested. If you earn UGX 1,000,000 per month, keep UGX 3,000,000-6,000,000 in a savings account or Money Market Fund, and invest the rest for growth.",
          "The key difference is time. Money you'll need within 6 months should be saved. Money you won't need for 1+ years should be invested. The longer you can leave money invested, the better your returns will be.",
        ],
        keyTakeaways: [
          "Savings = safety + accessibility, low returns (2-5%)",
          "Investing = growth potential, higher returns (9-22%)",
          "Keep 3-6 months expenses as savings",
          "Invest money you won't need for 1+ years",
        ],
      },
      {
        id: "basics-2",
        title: "Understanding Inflation in Uganda",
        summary: "Why your money loses value sitting idle and what to do about it.",
        readTime: 3,
        content: [
          "Inflation is the gradual increase in prices over time. In Uganda, inflation has averaged around 5-7% per year. This means UGX 1,000,000 today will only buy about UGX 930,000 worth of goods next year.",
          "If your money is sitting in a current account earning 0%, you're actually losing 5-7% of its purchasing power every year. Even a regular savings account at 3% leaves you behind inflation.",
          "To beat inflation, your money needs to earn at least 7% per year. This is why Money Market Funds (9-12%) and Balanced Funds (12-16%) are popular — they keep your money growing faster than prices rise.",
          "Think of inflation as a silent tax on idle money. Every shilling not invested is a shilling losing value. The best defense is putting your money to work in regulated investment products.",
        ],
        keyTakeaways: [
          "Uganda inflation averages 5-7% per year",
          "Money in a current account loses value every year",
          "You need at least 7% returns to beat inflation",
          "Money Market Funds (9-12%) beat inflation comfortably",
        ],
      },
      {
        id: "basics-3",
        title: "Budgeting with the 50/30/20 Rule",
        summary: "A simple framework to manage your income effectively.",
        readTime: 3,
        content: [
          "The 50/30/20 rule is a simple budgeting framework: spend 50% of your income on needs, 30% on wants, and save/invest 20%. For someone earning UGX 2,000,000 per month, that's UGX 1,000,000 for rent, food, transport; UGX 600,000 for entertainment and extras; and UGX 400,000 for savings and investments.",
          "In Uganda's context, 'needs' include rent, food, school fees, transport to work, utilities, and mobile money charges. 'Wants' are eating out, new clothes, entertainment, data bundles beyond basic needs. The 20% should go straight to your Everpesa wallet or a unit trust fund before you spend on anything else.",
          "Pay yourself first. The day you receive your salary, immediately move 20% to savings or investments. Don't wait to see what's left at the end of the month — there will never be anything left. Automate this with Xeno's auto-invest feature or regular mobile money deposits to your Everpesa wallet.",
          "If 20% feels impossible right now, start with 5% or 10%. The habit matters more than the amount. Once you see your money growing and earning returns, you'll naturally want to save more.",
        ],
        keyTakeaways: [
          "50% needs, 30% wants, 20% save/invest",
          "Pay yourself first — invest before spending",
          "Start small if needed (5-10%)",
          "Automate your savings for consistency",
        ],
      },
    ],
  },
  {
    id: "investing",
    label: "Smart Investing",
    icon: TrendingUpOutline,
    color: "#e65100",
    description: "Learn to grow your wealth wisely",
    articles: [
      {
        id: "invest-1",
        title: "Unit Trust Funds Explained Simply",
        summary: "How unit trusts work and why they're popular in Uganda.",
        readTime: 5,
        content: [
          "A unit trust fund pools money from many investors and invests it as one large fund. A professional fund manager decides where to invest — in government securities, bank deposits, company shares, or bonds. When you invest UGX 100,000 in a unit trust, you're buying 'units'. As the fund grows, the value of your units increases.",
          "In Uganda, unit trust funds are regulated by the Capital Markets Authority (CMA). Licensed fund managers include UAP Old Mutual, Stanbic (through SIMS), ICEA Lion, Sanlam, African Alliance, and digital platforms like Xeno. All must publish daily unit prices and quarterly reports.",
          "There are different types of funds for different goals:\n\n• Money Market Funds (9-12% p.a.) — invest in treasury bills and bank deposits. Lowest risk, easy to withdraw.\n• Bond/Income Funds (12-15% p.a.) — invest in government bonds. Moderate risk, higher returns.\n• Balanced Funds (12-16% p.a.) — mix of bonds and shares. Medium risk.\n• Equity Funds (14-22% p.a.) — invest in company shares. Highest risk, highest potential returns.",
          "Getting started is simple: choose a fund manager, complete their KYC process, deposit money via mobile money or bank transfer, and your money starts earning returns immediately. Most funds have no lock-in period for Money Market Funds — you can withdraw anytime.",
        ],
        keyTakeaways: [
          "Unit trusts pool money from many investors",
          "Managed by CMA-licensed professionals",
          "Different fund types for different risk levels",
          "Most Money Market Funds allow same-day withdrawal",
        ],
      },
      {
        id: "invest-2",
        title: "Understanding Risk and Return",
        summary: "Why higher returns come with higher risk, and how to choose your level.",
        readTime: 4,
        content: [
          "In investing, risk and return are always linked. Higher potential returns come with higher risk of losing money. A government treasury bill is very safe (backed by the government) but pays 10-12%. Company shares could return 20%+ but could also drop in value.",
          "Your risk tolerance depends on three things: your age (younger = can take more risk), your time horizon (longer = can take more risk), and your financial cushion (more savings = can take more risk). A 25-year-old saving for retirement in 35 years can invest heavily in equity funds. A 55-year-old nearing retirement should stick to money market and bond funds.",
          "Diversification is your best friend. Don't put all your money in one investment. Spread it across different funds and asset types. A common allocation for a medium-risk Ugandan investor:\n\n• 40% Money Market Fund (safety net)\n• 35% Balanced Fund (steady growth)\n• 25% Equity Fund (long-term growth)",
          "Never invest money you can't afford to lose in high-risk products. And never invest money you'll need in the next 6 months in anything other than a Money Market Fund or savings account.",
        ],
        keyTakeaways: [
          "Higher returns = higher risk, always",
          "Time horizon is your biggest advantage",
          "Diversify across fund types",
          "Never invest emergency money in risky products",
        ],
      },
      {
        id: "invest-3",
        title: "P2P Lending: How It Works on Everpesa",
        summary: "Earn returns by funding loans to vetted borrowers.",
        readTime: 4,
        content: [
          "Peer-to-peer (P2P) lending connects investors directly with borrowers. On Everpesa, borrowers apply for loans, our team vets them (KYC, credit scoring, business verification), and approved loans are listed for investors to fund.",
          "Each loan is divided into 'units' costing UGX 1,000 each. You can buy as few or as many units as you want. When the borrower repays their loan (principal + interest), repayments are distributed to all investors proportionally based on their units.",
          "Interest rates on Everpesa P2P loans range from 10-20% depending on the loan duration. Shorter loans (1-2 weeks) tend to have lower rates, while longer loans (2-3 months) offer higher returns. Each loan has a risk tier (A through E) assigned by our credit team.",
          "The key risk is borrower default. If a borrower fails to repay, investors may lose their capital. Everpesa mitigates this through rigorous borrower screening, but no investment is risk-free. Spread your investment across multiple loans rather than putting everything into one.",
        ],
        keyTakeaways: [
          "You fund loans to vetted borrowers",
          "Buy units at UGX 1,000 each",
          "Returns paid when borrower repays (10-20%)",
          "Diversify across multiple loans to reduce risk",
        ],
      },
    ],
  },
  {
    id: "loans",
    label: "Borrowing Wisely",
    icon: CashOutline,
    color: "#6a1b9a",
    description: "Use credit responsibly",
    articles: [
      {
        id: "loans-1",
        title: "Good Debt vs. Bad Debt",
        summary: "Not all borrowing is equal — learn the difference.",
        readTime: 3,
        content: [
          "Good debt is borrowing that creates value or income. Taking a loan to buy inventory for your shop, fund a business expansion, or purchase equipment that generates revenue — these are good debts because the return exceeds the interest cost.",
          "Bad debt is borrowing for consumption that doesn't generate returns. Taking a loan to buy the latest phone, fund a party, or cover lifestyle expenses — these are bad debts because you'll repay more than you borrowed with nothing to show for it.",
          "Before taking any loan, ask yourself: Will this loan make me more money than it costs? A UGX 1,000,000 loan at 15% interest costs UGX 150,000. If you use it to buy stock that generates UGX 300,000 profit, that's good debt. If you use it for a weekend trip, that's UGX 150,000 lost.",
          "Device financing can be good debt if the device is essential for your business (e.g., a phone for your boda-boda delivery business). But financing a luxury phone you don't need is bad debt disguised as a convenient payment plan.",
        ],
        keyTakeaways: [
          "Good debt generates more income than it costs",
          "Bad debt funds consumption with no return",
          "Always calculate: will the loan make me money?",
          "Device financing is only good if the device earns income",
        ],
      },
      {
        id: "loans-2",
        title: "How Interest Rates Work",
        summary: "Flat vs. reducing balance and what your loan really costs.",
        readTime: 4,
        content: [
          "When you borrow UGX 1,000,000 at 15% interest, you might assume you'll pay UGX 150,000 in interest. But the actual cost depends on how interest is calculated.",
          "Flat rate interest charges 15% on the original amount for the entire loan period. You pay UGX 150,000 interest regardless of how much principal you've repaid. Most short-term loans in Uganda use flat rates because they're simpler.",
          "Reducing balance interest charges 15% only on the outstanding balance. As you repay principal, the interest reduces. Over a 3-month loan, reducing balance works out to roughly half the cost of flat rate. Unit trust fund managers and banks often use reducing balance.",
          "Always ask your lender: 'Is this flat rate or reducing balance?' On Everpesa, interest is calculated on the original loan amount (flat rate) with the total clearly shown in your loan offer before you accept. No hidden charges.",
        ],
        keyTakeaways: [
          "Flat rate: interest on original amount (more expensive)",
          "Reducing balance: interest on outstanding amount (cheaper)",
          "Always check which method your lender uses",
          "Everpesa shows total cost upfront in the loan offer",
        ],
      },
    ],
  },
  {
    id: "security",
    label: "Security & Safety",
    icon: ShieldCheckmarkOutline,
    color: "#c62828",
    description: "Protect your money and accounts",
    articles: [
      {
        id: "security-1",
        title: "Spotting Financial Scams in Uganda",
        summary: "Common scams and how to protect yourself.",
        readTime: 4,
        content: [
          "If someone promises guaranteed returns above 30% per year, it's almost certainly a scam. Legitimate investments in Uganda return 8-22% depending on risk level. Ponzi schemes like the ones that collapsed in Uganda promise 'too-good-to-be-true' returns and pay old investors with new investor money until they collapse.",
          "Red flags to watch for: pressure to invest immediately ('limited time offer'), no CMA license, returns paid in cash with no paper trail, requests to recruit other investors for bonuses, inability to withdraw your money, and communication only through social media or WhatsApp groups.",
          "Always verify: Is the company licensed by the Capital Markets Authority (CMA) or Bank of Uganda? You can check at www.cmauganda.co.ug. Do they publish audited financial statements? Can you withdraw your money when you want? Are there published unit prices or account statements?",
          "If in doubt, ask. Contact CMA Uganda directly, or use Everpesa's Financial Advisor chat to ask about any investment opportunity you've been offered. Legitimate companies welcome scrutiny — scammers avoid it.",
        ],
        keyTakeaways: [
          "Returns above 30% p.a. are almost certainly scams",
          "Always check CMA licensing at cmauganda.co.ug",
          "Beware of pressure to invest immediately",
          "Legitimate companies welcome questions and audits",
        ],
      },
      {
        id: "security-2",
        title: "Keeping Your Everpesa Account Safe",
        summary: "Best practices for account security.",
        readTime: 3,
        content: [
          "Use a strong, unique password. Don't reuse passwords from other apps or websites. A good password is at least 8 characters with a mix of letters, numbers, and symbols. Avoid using your name, birthday, or phone number.",
          "Never share your login credentials, OTP codes, or wallet PIN with anyone — including people claiming to be from Everpesa. Our staff will never ask for your password or PIN. If someone does, it's a scam.",
          "Enable phone verification to add an extra layer of security to your account. Always keep your registered phone number up to date so you can receive security notifications and OTP codes.",
          "If you suspect unauthorized access to your account, immediately change your password and contact our support team. We can temporarily freeze your wallet while we investigate.",
        ],
        keyTakeaways: [
          "Use a strong, unique password (8+ characters)",
          "Never share credentials or OTP codes with anyone",
          "Everpesa staff will never ask for your password",
          "Report suspicious activity immediately",
        ],
      },
    ],
  },
];

// ─── Component ──────────────────────────────────────────────────
export const LearnPage = () => {
  useTitle("Financial Literacy");
  const [activeArticle, setActiveArticle] = useState<Article | null>(null);
  const [activeCategory, setActiveCategory] = useState<string | null>(null);
  const { getData, setData } = useLocalStorage();

  const readArticles: string[] = getData("readArticles") || [];
  const markAsRead = (id: string) => {
    if (!readArticles.includes(id)) {
      setData("readArticles", [...readArticles, id]);
    }
  };

  const totalArticles = CATEGORIES.reduce((sum, cat) => sum + cat.articles.length, 0);
  const readCount = readArticles.length;

  if (activeArticle) {
    return (
      <ArticleView
        article={activeArticle}
        onBack={() => setActiveArticle(null)}
        onRead={() => markAsRead(activeArticle.id)}
        isRead={readArticles.includes(activeArticle.id)}
      />
    );
  }

  return (
    <PageWrapper>
      <AnimateChildren>
        {/* Header */}
        <HeaderSection>
          <SchoolOutline fontSize={36} color="#fff" />
          <div className="font20 bold white mt5">Financial Literacy</div>
          <div className="font13 white" style={{ opacity: 0.85 }}>
            Practical financial education for Ugandans
          </div>
          <ProgressCard>
            <div className="flexSpaceCenter mb8">
              <div className="font12 semiBold textDark">Your Progress</div>
              <div className="font12 semiBold main">
                {readCount}/{totalArticles} read
              </div>
            </div>
            <ProgressBar>
              <ProgressFill style={{ width: `${totalArticles > 0 ? (readCount / totalArticles) * 100 : 0}%` }} />
            </ProgressBar>
          </ProgressCard>
        </HeaderSection>

        {/* Categories */}
        <div className="p15 flexColumn gap15">
          {CATEGORIES.map((cat) => {
            const isOpen = activeCategory === cat.id;
            const catRead = cat.articles.filter((a) => readArticles.includes(a.id)).length;

            return (
              <CategoryCard key={cat.id} className="r15 whiteBg hidden">
                <CatHeader onClick={() => setActiveCategory(isOpen ? null : cat.id)} className="pointer">
                  <IconCircle style={{ background: alpha(cat.color, 0.1), color: cat.color }}>
                    <cat.icon fontSize={18} />
                  </IconCircle>
                  <div className="flexGrow">
                    <div className="font14 semiBold textDark">{cat.label}</div>
                    <div className="font11 grey">{cat.description}</div>
                  </div>
                  <div className="flexNullCenter gap8">
                    <div className="font11 semiBold" style={{ color: cat.color }}>
                      {catRead}/{cat.articles.length}
                    </div>
                    <ChevronDown
                      fontSize={16}
                      className="grey"
                      style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                    />
                  </div>
                </CatHeader>

                {isOpen && (
                  <ArticleList>
                    {cat.articles.map((article) => {
                      const isRead = readArticles.includes(article.id);
                      return (
                        <ArticleRow key={article.id} onClick={() => setActiveArticle(article)} className="pointer animate">
                          <div className="flexGrow" style={{ minWidth: 0 }}>
                            <div className="flexNullCenter gap6 mb3">
                              {isRead && <CheckmarkCircle fontSize={14} color={PRIMARY_COLOR} />}
                              <div className={`font13 textDark ${!isRead ? "semiBold" : ""}`} style={{ opacity: isRead ? 0.65 : 1 }}>
                                {article.title}
                              </div>
                            </div>
                            <div className="font11 grey ellipsis">{article.summary}</div>
                          </div>
                          <div className="flexNullCenter gap5 font10 grey" style={{ flexShrink: 0 }}>
                            <TimeOutline fontSize={11} />
                            {article.readTime} min
                          </div>
                        </ArticleRow>
                      );
                    })}
                  </ArticleList>
                )}
              </CategoryCard>
            );
          })}
        </div>
      </AnimateChildren>
    </PageWrapper>
  );
};

// ─── Article Detail View ────────────────────────────────────────
const ArticleView = ({ article, onBack, onRead, isRead }: { article: Article; onBack: () => void; onRead: () => void; isRead: boolean }) => {
  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    if (el.scrollTop + el.clientHeight >= el.scrollHeight - 50) onRead();
  };

  return (
    <PageWrapper>
      <ArticleWrapper onScroll={handleScroll}>
        <BackRow onClick={onBack} className="pointer">← Back to articles</BackRow>

        <ArticleHeader>
          <div className="font18 bold textDark" style={{ lineHeight: 1.4 }}>{article.title}</div>
          <div className="flexNullCenter gap10 mt8">
            <div className="flexNullCenter gap5 font12 grey">
              <TimeOutline fontSize={13} />
              {article.readTime} min read
            </div>
            {isRead && (
              <div className="flexNullCenter gap5 font12 main">
                <CheckmarkCircle fontSize={13} />
                Read
              </div>
            )}
          </div>
        </ArticleHeader>

        <ArticleBody>
          {article.content.map((para, i) => (
            <p key={i} style={{ whiteSpace: "pre-line" }}>{para}</p>
          ))}
        </ArticleBody>

        <TakeawayBox>
          <div className="font13 semiBold textDark mb10 flexNullCenter gap6">
            <BulbOutline fontSize={16} color={PRIMARY_COLOR} />
            Key Takeaways
          </div>
          {article.keyTakeaways.map((t, i) => (
            <TakeawayItem key={i}>
              <CheckmarkCircle fontSize={14} color={PRIMARY_COLOR} style={{ flexShrink: 0, marginTop: 1 }} />
              <span>{t}</span>
            </TakeawayItem>
          ))}
        </TakeawayBox>

        <div style={{ height: 30 }} />
      </ArticleWrapper>
    </PageWrapper>
  );
};

// ─── Styles ─────────────────────────────────────────────────────
const PageWrapper = styled.div`
  max-width: 600px;
  height: 100%;
`;

const HeaderSection = styled.div`
  background: linear-gradient(135deg, ${PRIMARY_COLOR} 0%, ${alpha(PRIMARY_COLOR, 0.75)} 100%);
  padding: 30px 20px 24px;
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: 4px;
`;

const ProgressCard = styled.div`
  width: 100%;
  max-width: 300px;
  margin-top: 16px;
  padding: 14px 18px;
  background: rgba(255, 255, 255, 0.95);
  border-radius: 15px;
`;

const ProgressBar = styled.div`
  height: 6px;
  background: ${PRIMARY_LIGHT};
  border-radius: 3px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  background: ${PRIMARY_COLOR};
  border-radius: 3px;
  transition: width 0.3s ease;
`;

const CategoryCard = styled.div`
  border: 1px solid ${PRIMARY_LIGHT};
`;

const CatHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 18px;
`;

const IconCircle = styled.div`
  width: 42px;
  height: 42px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`;

const ArticleList = styled.div`
  border-top: 1px solid ${PRIMARY_LIGHT};
`;

const ArticleRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-bottom: 1px solid ${PRIMARY_LIGHT};

  &:last-child { border-bottom: none; }
  &:hover { background: ${primary5}; }
`;

const ArticleWrapper = styled.div`
  height: calc(100vh - 56px);
  overflow-y: auto;
  @media (max-width: 960px) { height: calc(100vh - 112px); }
`;

const BackRow = styled.div`
  padding: 16px 18px;
  font-size: 13px;
  color: ${PRIMARY_COLOR};
  font-weight: 600;
  border-bottom: 1px solid ${PRIMARY_LIGHT};
`;

const ArticleHeader = styled.div`
  padding: 22px 18px 0;
`;

const ArticleBody = styled.div`
  padding: 18px;
  p {
    font-size: 14px;
    line-height: 1.8;
    color: #333;
    margin-bottom: 18px;
    &:last-child { margin-bottom: 0; }
  }
`;

const TakeawayBox = styled.div`
  margin: 0 18px;
  padding: 18px;
  background: ${primary5};
  border: 1px solid ${PRIMARY_LIGHT};
  border-radius: 15px;
`;

const TakeawayItem = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-size: 13px;
  color: #333;
  line-height: 1.5;
  padding: 5px 0;
`;